import React from 'react';
import I18n from '@/modules/i18n';
import styles from '@/components/shared/Correlations/Correlations.module.scss';
import { LibraryType, TagType } from '@/components/shared/Correlations/types';
import useLibraryTagValues from '@/components/shared/Correlations/hooks/useLibraryTagValues';
import cn from '@/utils/cn';
import Text from '@/components/ui/Text';
import Icon from '@/components/ui/Icon';
import { COLORS } from '@/components/ui/ui.constants';
import { outlineStyles } from '@/components/shared/Correlations/TagCard/shared';

type LibraryTagContentType = {
  isExpanded: boolean;
  library: LibraryType;
  programId: number;
  tag: TagType;
  toggleExpanded: (key: string) => void;
};

// A library row (Biographies, Primary Source Library, …) on a card: the reference materials in that
// library tagged with the standard. Same row anatomy as UnitTagContent / LessonTagContent.
const LibraryTagContent = ({ isExpanded, library, programId, tag, toggleExpanded }: LibraryTagContentType) => {
  const { data: tagValues, isError, isLoading } = useLibraryTagValues({
    isEnabled: isExpanded, libraryTagId: library.id, programId, tagId: tag.id,
  });
  const referenceMaterials = tagValues.referenceMaterials ?? [];
  const loaded = !isLoading && !isError;
  const buttonId = `library-${tag.id}-${library.id}-button`;
  const contentId = `library-${tag.id}-${library.id}-content`;

  return (
    <div className={styles.lesson}>
      <Text asChild size="sm" weight={isExpanded ? 'bold' : 'normal'}>
        <button
          aria-controls={contentId}
          aria-expanded={isExpanded}
          className={cn(
            `tw-bg-transparent tw-border-none tw-cursor-pointer tw-outline-none
                   hover:tw-bg-white hover:tw-bg-opacity-25 focus:tw-bg-white focus:tw-bg-opacity-25
                   focus:tw-outline-none`,
            outlineStyles,
            styles.tagButton,
          )}
          id={buttonId}
          type="button"
          onClick={() => toggleExpanded(`Library-${library.id}`)}
        >
          <Icon
            className="tw-mr-[8px] tw-inline-block tw-h-[length:1lh] tw-align-top"
            fill={COLORS['very-dark-grey']}
            icon={isExpanded ? 'ChevronDown' : 'ChevronRight'}
            size={12}
          />
          {library.label}
          {isExpanded && isLoading && <i className="fa fa-spinner fa-spin tw-ml-[8px]" />}
        </button>
      </Text>
      {isExpanded && (
        <div className="tw-ml-[20px] tw-mt-[8px]" id={contentId}>
          {isLoading && (
            <div>{I18n.t('loading_tag_values')}</div>
          )}

          {isError && (
            <div>{I18n.t('error_loading_reference_materials')}</div>
          )}

          {loaded && referenceMaterials.length === 0 && (
            <div>{I18n.t('no_content_for_tag')}</div>
          )}

          {loaded && referenceMaterials.length > 0 && (
            <ul aria-labelledby={buttonId} className="tw-list-disc">
              {referenceMaterials.map(referenceMaterial => (
                <li key={referenceMaterial.id}>
                  <Text asChild size="sm">
                    <a
                      className={outlineStyles}
                      href={referenceMaterial.href}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      {referenceMaterial.label}
                      <span className="tw-sr-only">, {library.label}</span>
                    </a>
                  </Text>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default LibraryTagContent;
